import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useEmailHealth,
  useRecordEmailMetrics,
} from '../../hooks/useAdminExtended';

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

function getRateColor(rate: number, inverse = false): string {
  const threshold = inverse ? 1 - rate : rate;
  if (threshold < 0.02) return inverse ? '#d32f2f' : '#2e7d32';
  if (threshold < 0.05) return inverse ? '#ed6c02' : '#ed6c02';
  return inverse ? '#2e7d32' : '#d32f2f';
}

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateStr));
}

interface MetricCardProps {
  label: string;
  value: string;
  color: string;
  subtitle?: string;
  loading?: boolean;
}

function MetricCard({ label, value, color, subtitle, loading }: MetricCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: 12, sm: 13 }, mb: 1 }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width="60%" height={36} />
        ) : (
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            {value}
          </Typography>
        )}
        {subtitle && !loading && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: 11, sm: 12 } }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function EmailHealthPage() {
  const { data, isLoading, error } = useEmailHealth(30);
  const recordMutation = useRecordEmailMetrics();

  const summary = data?.summary;
  const history = data?.history;

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load email health metrics.
      </Alert>
    );
  }

  const bounceWarning =
    summary && summary.bounceRate >= 0.05;
  const complaintWarning =
    summary && summary.complaintRate >= 0.02;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Email Health
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => recordMutation.mutate()}
          disabled={recordMutation.isPending}
          sx={{ minHeight: 44 }}
        >
          Record Now
        </Button>
      </Box>

      {/* Warnings */}
      {bounceWarning && (
        <Alert severity="error" sx={{ mb: 2 }}>
          High bounce rate detected: {formatPercent(summary.bounceRate)}. Review
          your email lists and sending practices.
        </Alert>
      )}
      {complaintWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Elevated complaint rate: {formatPercent(summary.complaintRate)}.
          Monitor for potential deliverability issues.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="Delivery Rate"
            value={summary ? formatPercent(summary.deliveryRate) : '--'}
            color={
              summary
                ? getRateColor(summary.deliveryRate, true)
                : 'text.primary'
            }
            subtitle={
              summary
                ? `${summary.totalDelivered} / ${summary.totalSent}`
                : undefined
            }
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="Bounce Rate"
            value={summary ? formatPercent(summary.bounceRate) : '--'}
            color={
              summary ? getRateColor(summary.bounceRate) : 'text.primary'
            }
            subtitle={
              summary
                ? `${summary.totalBounced} bounced`
                : undefined
            }
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="Complaint Rate"
            value={summary ? formatPercent(summary.complaintRate) : '--'}
            color={
              summary
                ? getRateColor(summary.complaintRate)
                : 'text.primary'
            }
            subtitle={
              summary
                ? `${summary.totalComplaints} complaints`
                : undefined
            }
            loading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MetricCard
            label="Open Rate"
            value={summary ? formatPercent(summary.openRate) : '--'}
            color={
              summary
                ? getRateColor(summary.openRate, true)
                : 'text.primary'
            }
            subtitle={
              summary
                ? `${summary.totalOpened} opened`
                : undefined
            }
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Daily Metrics History */}
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.15rem' } }}
      >
        Daily Metrics (Last 30 Days)
      </Typography>

      {isLoading ? (
        <Stack spacing={1.5}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" height={80} />
          ))}
        </Stack>
      ) : history && history.length > 0 ? (
        <Stack spacing={1.5}>
          {history.map((metric) => {
            const dayBounceRate =
              metric.totalSent > 0
                ? metric.totalBounced / metric.totalSent
                : 0;
            const dayDeliveryRate =
              metric.totalSent > 0
                ? metric.totalDelivered / metric.totalSent
                : 0;

            return (
              <Card key={metric.id} variant="outlined">
                <CardContent
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ fontSize: { xs: 13, sm: 14 } }}
                    >
                      {formatDate(metric.date)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: 11, sm: 12 } }}
                    >
                      {metric.totalSent} sent
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { xs: 1.5, sm: 3 },
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 11 }}
                      >
                        Delivered
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          fontSize: { xs: 13, sm: 14 },
                          color: getRateColor(dayDeliveryRate, true),
                        }}
                      >
                        {metric.totalDelivered} ({formatPercent(dayDeliveryRate)})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 11 }}
                      >
                        Bounced
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          fontSize: { xs: 13, sm: 14 },
                          color: getRateColor(dayBounceRate),
                        }}
                      >
                        {metric.totalBounced} ({formatPercent(dayBounceRate)})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 11 }}
                      >
                        Opened
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: 13, sm: 14 } }}
                      >
                        {metric.totalOpened}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 11 }}
                      >
                        Clicked
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: 13, sm: 14 } }}
                      >
                        {metric.totalClicked}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 11 }}
                      >
                        Complaints
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          fontSize: { xs: 13, sm: 14 },
                          color:
                            metric.totalComplaints > 0
                              ? '#d32f2f'
                              : 'text.primary',
                        }}
                      >
                        {metric.totalComplaints}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 6 }}
        >
          No daily metrics recorded yet. Click "Record Now" to capture
          today's metrics.
        </Typography>
      )}
    </Box>
  );
}
